import React, { useCallback, useMemo, useState } from 'react';
import {
  KB_URL_GROUP_THRESHOLD,
  getHostnameKey,
  normalizeUrlString,
  parseStoredUrls,
  serializeUrls,
  splitBulkText,
  urlPillLabel,
} from 'utils/knowledgeBaseUrls';
export default function KnowledgeBaseUrlsInput({ value, onChange, t }) {
  const [draft, setDraft] = useState('');
  const urls = useMemo(() => parseStoredUrls(value), [value]);

  const draftNormalized = useMemo(() => {
    const d = draft.trim();
    if (!d) return null;
    return normalizeUrlString(d);
  }, [draft]);

  const draftHasError = draft.trim().length > 0 && !draftNormalized;

  const setUrlsArray = useCallback(
    (next) => {
      onChange(serializeUrls(next));
    },
    [onChange]
  );

  const mergeInUrls = useCallback(
    (candidates) => {
      const seen = new Set(urls);
      const next = [...urls];
      for (const raw of candidates) {
        const n = normalizeUrlString(raw);
        if (!n || seen.has(n)) continue;
        seen.add(n);
        next.push(n);
      }
      if (next.length !== urls.length) setUrlsArray(next);
    },
    [urls, setUrlsArray]
  );

  const addUrlsFromText = useCallback(
    (text) => {
      const parts = splitBulkText(text);
      mergeInUrls(parts);
    },
    [mergeInUrls]
  );

  const handleAdd = useCallback(() => {
    if (!draftNormalized) return;
    if (urls.includes(draftNormalized)) {
      setDraft('');
      return;
    }
    setUrlsArray([...urls, draftNormalized]);
    setDraft('');
  }, [draftNormalized, urls, setUrlsArray]);

  const handleRemove = useCallback(
    (removeHref) => {
      setUrlsArray(urls.filter((u) => u !== removeHref));
    },
    [urls, setUrlsArray]
  );

  const grouped = useMemo(() => {
    if (urls.length < KB_URL_GROUP_THRESHOLD) return null;
    const map = new Map();
    for (const u of urls) {
      const key = getHostnameKey(u);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(u);
    }
    return Array.from(map.entries()).sort((a, b) =>
      a[0].localeCompare(b[0], undefined, { sensitivity: 'base' })
    );
  }, [urls]);

  const handleBulkZoneClick = useCallback(async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.readText) {
        const text = await navigator.clipboard.readText();
        addUrlsFromText(text);
      }
    } catch {
      /* permission or unsupported */
    }
  }, [addUrlsFromText]);

  const handleBulkPaste = useCallback(
    (e) => {
      e.preventDefault();
      const text = e.clipboardData?.getData('text/plain') || '';
      addUrlsFromText(text);
    },
    [addUrlsFromText]
  );

  const handleBulkKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleBulkZoneClick();
      }
    },
    [handleBulkZoneClick]
  );

  const countLabel =
    urls.length === 1 ? `1 ${t('URL')}` : `${urls.length} ${t('URLs')}`;

  return (
    <div className="kb-url-field mb-3">
      <div className="kb-url-label-row">
        <label className="label-title mb-0">{t('Base de connaissance en ligne')}</label>
        <span className="kb-url-count-badge" aria-live="polite">
          {countLabel}
        </span>
      </div>

      <div className="kb-url-input-row">
        <input
          type="text"
          className={`form-control kb-url-input ${draftHasError ? 'is-invalid' : ''}`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder="https://example.com"
          autoComplete="off"
          spellCheck={false}
          aria-invalid={draftHasError}
        />
        <button
          type="button"
          className="kb-url-add-btn"
          onClick={handleAdd}
          disabled={!draftNormalized}
        >
          + {t('Ajouter')}
        </button>
      </div>
      {draftHasError && (
        <div className="kb-url-input-hint text-danger small mb-2">{t('URL invalide')}</div>
      )}

      <div
        className="kb-url-bulk-zone"
        role="button"
        tabIndex={0}
        onClick={handleBulkZoneClick}
        onPaste={handleBulkPaste}
        onKeyDown={handleBulkKeyDown}
      >
        <span className="kb-url-bulk-icon" aria-hidden>
          ↳
        </span>
        <span className="kb-url-bulk-text">
          {t("Coller plusieurs URLs d'un coup — séparées par des virgules ou sauts de ligne")}
        </span>
      </div>

      <div className="kb-url-tags-scroll">
        {urls.length === 0 ? (
          <div className="kb-url-empty">{t('Aucune URL ajoutée pour le moment')}</div>
        ) : !grouped ? (
          <div className="kb-url-pill-wrap">
            {urls.map((u) => (
              <span key={u} className="kb-url-pill" title={u}>
                <span className="kb-url-pill-label">{urlPillLabel(u)}</span>
                <button
                  type="button"
                  className="kb-url-pill-remove"
                  onClick={() => handleRemove(u)}
                  aria-label={t('Supprimer')}
                >
                  <img src="/Images/close-iconnn.png" alt="" width={12} height={12} />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <div className="kb-url-grouped">
            {grouped.map(([domain, list]) => (
              <div key={domain} className="kb-url-domain-block">
                <div className="kb-url-domain-heading">{domain}</div>
                <div className="kb-url-pill-wrap">
                  {list.map((u) => (
                    <span key={u} className="kb-url-pill" title={u}>
                      <span className="kb-url-pill-label">{urlPillLabel(u)}</span>
                      <button
                        type="button"
                        className="kb-url-pill-remove"
                        onClick={() => handleRemove(u)}
                        aria-label={t('Supprimer')}
                      >
                        <img src="/Images/close-iconnn.png" alt="" width={12} height={12} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
